import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ClarityModule } from '@clr/angular';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LOCALE_ID } from '@angular/core';
import { StatisticsRange } from '../../models/broadcast-statistic';
import { CHANNEL_ORDER } from '../programplan/channel-order';
import { WhisperStatisticsComponent } from './whisper/whisper-statistics.component';
import { VoxtralStatisticsComponent } from './voxtral/voxtral-statistics.component';

type PeriodType = 'year' | 'month' | 'day';
type StatisticsTab = 'whisper' | 'voxtral';

/**
 * Statistik-Seite: Rahmen um zwei Tabs.
 *
 * 2026-08-24 — die Seite zeigte bisher nur die Whisper-/LIVE-Sicht. Sie ist
 * jetzt in Shell + zwei Tab-Komponenten zerlegt: die Shell besitzt Sender-
 * filter und Zeitraum und reicht beides als ein StatisticsRange-Objekt nach
 * unten, jeder Tab lädt und exportiert seine eigenen Daten.
 *
 * Beide Tabs bleiben instanziiert (statt Clarity's *clrIfActive, das den
 * Inhalt beim Wechsel zerstört) — sonst würde jeder Tabwechsel eine volle
 * Abfrage über alle Tage des Zeitraums erneut auslösen. Stattdessen lädt ein
 * Tab nur, während er aktiv ist, und merkt sich den zuletzt geladenen Range.
 */
@Component({
  selector: 'app-statistics',
  standalone: true,
  imports: [CommonModule, FormsModule, ClarityModule, WhisperStatisticsComponent, VoxtralStatisticsComponent],
  providers: [{ provide: LOCALE_ID, useValue: 'de-DE' }],
  templateUrl: './statistics.component.html',
  styleUrl: './statistics.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class StatisticsComponent implements OnInit {

  activeTab: StatisticsTab = 'whisper';

  periodType: PeriodType = 'day';

  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth();
  selectedDate: Date = new Date();
  selectedChannels: string[] = [];

  fromDate!: Date;
  toDate!: Date;
  channelFilter: string = 'ORFS';

  /** Zeitraum + Sender für die Tabs; neue Objektidentität = neu laden. */
  range: StatisticsRange | null = null;

  ngOnInit(): void {
    this.activateAllFilters(false);

    // Initiale Ladung starten
    this.periodType = 'month';
    this.onMonthChange(String(this.selectedMonth));
  }

  selectTab(tab: StatisticsTab) {
    this.activeTab = tab;
  }

  onPeriodTypeChange(type: PeriodType) {
    this.periodType = type;
    this.recalculateRange();
  }

  onYearChange(year: number) {
    this.selectedYear = Number(year);
    this.recalculateRange();
  }

  onMonthChange(monthStr: string) {
    this.selectedMonth = Number(monthStr);
    this.recalculateRange();
  }

  onDateChange(value: string | Date) {
    let parsedDate: Date;

    if (typeof value === 'string') {
      const [day, month, year] = value.split('.');
      if (day && month && year) {
        parsedDate = new Date(Date.UTC(+year, +month - 1, +day));
      } else {
        parsedDate = new Date();
      }
    } else {
      // Wenn es ein Date-Objekt ist, in UTC-Midnight konvertieren
      parsedDate = new Date(Date.UTC(
        value.getFullYear(),
        value.getMonth(),
        value.getDate()
      ));
    }

    this.selectedDate = parsedDate;
    this.recalculateRange();
  }

  private recalculateRange() {
    switch (this.periodType) {
      case 'year': {
        const y = this.selectedYear;
        this.fromDate = new Date(Date.UTC(y, 0, 1, 0, 0, 0, 0));
        this.toDate = new Date(Date.UTC(y, 11, 31, 23, 59, 59, 999));
        break;
      }
      case 'month': {
        const y = this.selectedYear;
        const m = this.selectedMonth - 1; // JS: 0–11
        this.fromDate = new Date(Date.UTC(y, m, 1, 0, 0, 0, 0));
        // Tag 0 des Folgemonats = letzter Tag des aktuellen Monats
        this.toDate = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999));
        break;
      }
      case 'day': {
        const d = new Date(this.selectedDate);
        this.fromDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0));
        this.toDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999));
        break;
      }
    }

    this.publishRange();
  }

  /**
   * Baut den Range für die Tabs neu auf.
   *
   * 2026-08-24 — das exklusive Ende wird aus den UTC-Kalenderfeldern von
   * toDate gebildet und auf Mitternacht gesetzt. Vorher wurde auf toDate
   * (23:59:59.999 UTC) ein Tag addiert; der Service formatiert aber mit den
   * LOKALEN Feldern, sodass die Zeit über Mitternacht kippte und das Backend
   * einen Tag zu viel abfragte ("Tag 15.07." lieferte den 15. UND den 16.).
   */
  private publishRange() {
    this.channelFilter = this.selectedChannels.join(',');

    const toDateExclusive = new Date(Date.UTC(
      this.toDate.getUTCFullYear(),
      this.toDate.getUTCMonth(),
      this.toDate.getUTCDate() + 1,
      0, 0, 0, 0
    ));

    this.range = {
      fromDate: this.fromDate,
      toDate: this.toDate,
      toDateExclusive,
      channelFilter: this.channelFilter,
    };
  }

  // 2026-09-07 — war eine wortgleiche Kopie von CHANNEL_ORDER. Zwei Listen, die
  // auseinanderlaufen können, sind eine Fehlerquelle mehr als nötig.
  get availableChannels(): string[] {
    return [...CHANNEL_ORDER];
  }

  toggleChannel(channel: string) {
    const idx = this.selectedChannels.indexOf(channel);
    if (idx >= 0) {
      this.selectedChannels.splice(idx, 1);
    } else {
      this.selectedChannels.push(channel);
    }
    this.recalculateRange();
  }

  deactivateAllFilters() {
    this.selectedChannels = []; // alle Sender aus
    this.recalculateRange();
  }

  activateAllFilters(recalculate: boolean = true) {
    this.selectedChannels = [...this.availableChannels]; // alle Sender aktiv
    this.recalculateRange();
  }

  getIconFilename(broadcast: { channel: string }): string {
    let extension = 'png';
    if (broadcast.channel === 'ORF1' || broadcast.channel === 'ORF2' || broadcast.channel === 'ORF3' || broadcast.channel === 'KIDS' || broadcast.channel === 'ORFS') {
      extension = 'svg';
    }
    let channel = broadcast.channel;
    if (channel.includes('-')) {
      const parts = channel.split('-');
      if (parts.length > 1) {
        channel = parts[1];
      }
    }
    return `assets/${channel}.${extension}`;
  }
}
